import * as React from "react";

import {
  concatStyleSets,
  IButtonStyles,
  ITheme,
  memoizeFunction,
} from "@fluentui/react";

import {
  AppFlyoutAnchor,
  AppFlyoutAnchorInternalProps,
  getActionButtonStyles,
  getActionFlyoutAnchorStyles,
} from "@1js/acui-button";
// eslint-disable-next-line no-restricted-imports
import { useNextRibbonMenuButtonEnabled } from "@1js/acui-button/lib/Next";
import { getModernUxExperimentPreference } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { MeasuredContext } from "@1js/acui-resize-group";
import { shouldShadowRender } from "@1js/acui-shadow-rendering";
import { AppThemeConsumer } from "@1js/acui-theme";

import { getModernUxRibbonFlyoutAnchorStyles } from "../../../../styling/modernUxStyles";
import { getOnRenderText } from "../onRenderText";
import { UseLegacyRibbonButtonProps } from "../RibbonButton";
import { ribbonButtonHighContrastStyles } from "../RibbonButton/RibbonButton.styles";
import { RibbonControlInternalProps } from "../RibbonControlProps";
import { shouldPersistMenu } from "../shouldPersistMenu";
import { RibbonFlyoutAnchor as RibbonFlyoutAnchorNext } from "./next";

export type RibbonFlyoutAnchorInternalProps = AppFlyoutAnchorInternalProps &
  RibbonControlInternalProps &
  UseLegacyRibbonButtonProps & { legacyRef?: React.RefObject<AppFlyoutAnchor> };

const getStyles = memoizeFunction(
  (
    actionButtonStyles: IButtonStyles,
    flyoutAnchorStyles: IButtonStyles,
    customStyles?: IButtonStyles,
    modernUxButtonStyles?: IButtonStyles
  ) => {
    if (getModernUxExperimentPreference()) {
      return concatStyleSets(
        actionButtonStyles,
        flyoutAnchorStyles,
        ribbonButtonHighContrastStyles,
        modernUxButtonStyles,
        customStyles
      );
    }
    return concatStyleSets(
      actionButtonStyles,
      flyoutAnchorStyles,
      ribbonButtonHighContrastStyles,
      customStyles
    );
  }
);

export const getRibbonFlyoutAnchorStyles = (
  theme: ITheme,
  hideChevron?: boolean,
  customStyles?: IButtonStyles,
  modernUxButtonStyles?: IButtonStyles
): IButtonStyles => {
  return getStyles(
    getActionButtonStyles(theme),
    getActionFlyoutAnchorStyles(theme, !!hideChevron),
    customStyles,
    modernUxButtonStyles
  );
};

export const RibbonFlyoutAnchorCurrent = React.memo(
  React.forwardRef(
    (
      props: RibbonFlyoutAnchorInternalProps,
      ref: React.RefObject<AppFlyoutAnchor>
    ) => {
      const { shadowRenderMenu } = props;

      return (
        <MeasuredContext.Consumer>
          {({ isMeasured }) => (
            <AppContext.Consumer>
              {(context) => (
                <AppThemeConsumer>
                  {(theme: ITheme) => (
                    <AppFlyoutAnchor
                      {...props}
                      menuDefinition={props.menuDefinition}
                      styles={getRibbonFlyoutAnchorStyles(
                        theme,
                        props.hideChevron,
                        (props as AppFlyoutAnchorInternalProps).styles,
                        getModernUxRibbonFlyoutAnchorStyles()
                      )}
                      ref={props.legacyRef || ref}
                      dataAutomationType={
                        props.dataAutomationType || "RibbonFlyoutAnchor"
                      }
                      onRenderText={
                        props.onRenderText || getOnRenderText(props.size)
                      }
                      persistMenu={shouldPersistMenu(
                        context,
                        props.menuDefinition
                      )}
                      shadowRenderMenu={shouldShadowRender(
                        shadowRenderMenu,
                        context,
                        isMeasured
                      )}
                      preventCalloutDismissOnScroll={true}
                    />
                  )}
                </AppThemeConsumer>
              )}
            </AppContext.Consumer>
          )}
        </MeasuredContext.Consumer>
      );
    }
  )
);
RibbonFlyoutAnchorCurrent.displayName = "RibbonFlyoutAnchorCurrent";

export const RibbonFlyoutAnchor = React.forwardRef<
  HTMLButtonElement,
  RibbonFlyoutAnchorInternalProps
>((props, ref) => {
  const isNewButtonEnabled =
    useNextRibbonMenuButtonEnabled() && !props.useLegacyButton;
  return isNewButtonEnabled ? (
    <RibbonFlyoutAnchorNext ref={ref} {...props} />
  ) : (
    <RibbonFlyoutAnchorCurrent ref={ref as any} {...props} />
  );
});

RibbonFlyoutAnchor.displayName = "RibbonFlyoutAnchor";

export type RibbonFlyoutAnchorType = typeof RibbonFlyoutAnchor.prototype;
